import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { WIDGET_REGISTRY } from '../widgets/registry'
import { presetToSpan } from '../layout'
import KPI_SECTIONS from '../../governance/kpi/sections'
import LayoutCanvas from './LayoutCanvas'

const widgetUid = (widget, index) => widget._id ?? widget._uid ?? `widget-${index}`

const kpiSlugLabel = (slug) =>
  KPI_SECTIONS.find((section) => section.slug === slug)?.name ?? slug

// Compact sortable row in the sidebar widget list: drag to set the first-fit
// order, edit config, remove. Placement and size live on the canvas.
const WidgetRow = ({ uid, widget, onChange, onRemove, kpiSlugs }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: uid })
  const definition = WIDGET_REGISTRY[widget.type]
  if (!definition) return null

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        p: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Box
          {...attributes}
          {...listeners}
          sx={{ cursor: 'grab', display: 'flex', color: 'text.secondary' }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', flex: 1 }} noWrap>
          {definition.label}
        </Typography>
        <Tooltip title="Remove widget">
          <IconButton size="small" onClick={onRemove}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {(definition.configFields ?? []).length > 0 && (
        <Stack gap={1} sx={{ mt: 1 }}>
          {definition.configFields.map((field) => {
            const value = widget.config?.[field.key] ?? ''
            const setValue = (next) =>
              onChange({
                ...widget,
                config: { ...widget.config, [field.key]: next },
              })

            if (field.type === 'kpiSlug') {
              return (
                <Select
                  key={field.key}
                  size="small"
                  fullWidth
                  value={value || kpiSlugs[0] || ''}
                  onChange={(event) => setValue(event.target.value)}
                >
                  {kpiSlugs.map((slug) => (
                    <MenuItem key={slug} value={slug}>
                      {kpiSlugLabel(slug)}
                    </MenuItem>
                  ))}
                </Select>
              )
            }
            return (
              <TextField
                key={field.key}
                size="small"
                fullWidth
                label={field.label}
                value={value}
                multiline={field.type === 'multiline'}
                maxRows={3}
                onChange={(event) => setValue(event.target.value)}
              />
            )
          })}
        </Stack>
      )}
    </Paper>
  )
}

const ScreenEditor = ({ draft, updateDraft, kpiSlugs }) => {
  const [screenIndex, setScreenIndex] = useState(0)
  const [pickerOpen, setPickerOpen] = useState(false)

  const screens = draft.screens ?? []
  const activeIndex = Math.min(screenIndex, Math.max(screens.length - 1, 0))
  const screen = screens[activeIndex]

  const updateScreens = (nextScreens) =>
    updateDraft((current) => ({ ...current, screens: nextScreens }))

  const updateScreen = (updater) =>
    updateDraft((current) => ({
      ...current,
      screens: (current.screens ?? []).map((candidate, index) =>
        index === activeIndex ? updater(candidate) : candidate,
      ),
    }))

  const widgetIds = useMemo(
    () => (screen?.widgets ?? []).map(widgetUid),
    [screen],
  )

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    const from = widgetIds.indexOf(active.id)
    const to = widgetIds.indexOf(over.id)
    if (from < 0 || to < 0) return
    updateScreen((current) => ({
      ...current,
      widgets: arrayMove(current.widgets, from, to),
    }))
  }

  const addWidget = (type) => {
    const definition = WIDGET_REGISTRY[type]
    const span = presetToSpan(definition.defaultSize)
    updateScreen((current) => ({
      ...current,
      widgets: [
        ...(current.widgets ?? []),
        {
          _uid: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type,
          cols: span.cols,
          rows: span.rows,
          config: type === 'kpi' ? { slug: kpiSlugs[0] } : {},
        },
      ],
    }))
    setPickerOpen(false)
  }

  const addScreen = () => {
    updateScreens([
      ...screens,
      { name: `Screen ${screens.length + 1}`, dwellSeconds: 30, widgets: [] },
    ])
    setScreenIndex(screens.length)
  }

  const removeScreen = (index) => {
    updateScreens(screens.filter((candidate, i) => i !== index))
    setScreenIndex(0)
  }

  const moveScreen = (index, direction) => {
    const to = index + direction
    if (to < 0 || to >= screens.length) return
    updateScreens(arrayMove(screens, index, to))
    setScreenIndex(to)
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
      {/* Sidebar: screens, then this screen's widgets */}
      <Stack gap={2} sx={{ width: { xs: '100%', md: 290 }, flexShrink: 0 }}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 600 }}>Screens</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addScreen}
              sx={{ textTransform: 'none' }}
            >
              Add
            </Button>
          </Stack>
          <Stack gap={0.75} sx={{ mt: 1 }}>
            {screens.map((candidate, index) => (
              <Paper
                key={candidate._id ?? index}
                variant="outlined"
                onClick={() => setScreenIndex(index)}
                sx={{
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  borderColor: index === activeIndex ? 'primary.main' : undefined,
                }}
              >
                <Typography sx={{ flex: 1, fontSize: '0.9rem' }} noWrap>
                  {candidate.name || `Screen ${index + 1}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(candidate.widgets ?? []).length}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    moveScreen(index, -1)
                  }}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    moveScreen(index, 1)
                  }}
                >
                  <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={screens.length <= 1}
                  onClick={(event) => {
                    event.stopPropagation()
                    removeScreen(index)
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        </Paper>

        {screen && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={{ fontWeight: 600 }}>Widgets</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setPickerOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Add
              </Button>
            </Stack>
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={widgetIds} strategy={verticalListSortingStrategy}>
                <Stack gap={0.75} sx={{ mt: 1 }}>
                  {(screen.widgets ?? []).map((widget, index) => (
                    <WidgetRow
                      key={widgetUid(widget, index)}
                      uid={widgetUid(widget, index)}
                      widget={widget}
                      kpiSlugs={kpiSlugs}
                      onChange={(next) =>
                        updateScreen((current) => ({
                          ...current,
                          widgets: current.widgets.map((candidate, i) =>
                            i === index ? next : candidate,
                          ),
                        }))
                      }
                      onRemove={() =>
                        updateScreen((current) => ({
                          ...current,
                          widgets: current.widgets.filter(
                            (candidate, i) => i !== index,
                          ),
                        }))
                      }
                    />
                  ))}
                  {(screen.widgets ?? []).length === 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 1.5, textAlign: 'center' }}
                    >
                      No widgets yet — add some, then arrange and resize them
                      on the canvas.
                    </Typography>
                  )}
                </Stack>
              </SortableContext>
            </DndContext>
          </Paper>
        )}
      </Stack>

      {/* Main area: screen settings + the layout canvas */}
      {screen && (
        <Stack gap={1.5} sx={{ flex: 1, width: '100%', minWidth: 0 }}>
          <Stack direction="row" gap={1.5}>
            <TextField
              size="small"
              label="Screen name"
              value={screen.name ?? ''}
              onChange={(event) =>
                updateScreen((current) => ({ ...current, name: event.target.value }))
              }
              inputProps={{ maxLength: 60 }}
            />
            <TextField
              size="small"
              type="number"
              label="Show for (seconds)"
              value={screen.dwellSeconds ?? 30}
              onChange={(event) =>
                updateScreen((current) => ({
                  ...current,
                  dwellSeconds: Number(event.target.value),
                }))
              }
              inputProps={{ min: 10, max: 600 }}
              sx={{ width: 150 }}
            />
          </Stack>

          <LayoutCanvas
            boardId={draft._id}
            screen={screen}
            updateScreen={updateScreen}
            themeMode={draft.settings?.theme ?? 'dark'}
          />
        </Stack>
      )}

      {/* Widget picker */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a widget</DialogTitle>
        <DialogContent>
          <Stack gap={1} sx={{ mt: 0.5 }}>
            {Object.entries(WIDGET_REGISTRY).map(([type, definition]) => (
              <Paper
                key={type}
                variant="outlined"
                onClick={() => addWidget(type)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>{definition.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {definition.description}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  )
}

export default ScreenEditor
